import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ImagePlus, X } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const emptyForm = { name_nl: "", name_en: "", description_nl: "", description_en: "", price: 0, duration: 30, order: 0, is_active: true, image_url: null };

export default function AdminServices() {
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const load = () => api.get("/admin/services").then(({ data }) => setServices(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, order: services.length }); setError(""); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name_nl: s.name_nl, name_en: s.name_en, description_nl: s.description_nl, description_en: s.description_en, price: s.price, duration: s.duration, order: s.order, is_active: s.is_active, image_url: s.image_url || null }); setError(""); setDialogOpen(true); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("section", "services");
    formData.append("alt_text_nl", form.name_nl || "");
    formData.append("alt_text_en", form.name_en || "");
    formData.append("order", "0");
    try {
      const { data } = await api.post("/admin/photos", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image_url: data.url });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/services/${editing.id}`, form);
      else await api.post("/admin/services", form);
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/services/${s.id}`, { name_nl: s.name_nl, name_en: s.name_en, description_nl: s.description_nl, description_en: s.description_en, price: s.price, duration: s.duration, order: s.order, is_active: !s.is_active });
    load();
  };

  const handleDelete = async () => {
    await api.delete(`/admin/services/${deleteId}`);
    setDeleteId(null);
    load();
  };

  return (
    <div data-testid="admin-services-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl text-white">{t("admin.services.title")}</h1>
        <Button data-testid="admin-services-add-button" onClick={openNew} className="bg-gold text-white hover:bg-gold-hover rounded-none">
          <Plus size={16} className="mr-1.5" /> {t("admin.services.addService")}
        </Button>
      </div>

      {services.length === 0 && <p className="text-white/40" data-testid="services-empty-admin">{t("admin.services.noServices")}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.id} data-testid={`admin-service-card-${s.id}`} className={`bg-[#141414] border rounded-lg overflow-hidden ${s.is_active ? "border-white/10" : "border-white/5 opacity-50"}`}>
            {s.image_url && (
              <img data-testid={`service-image-${s.id}`} src={`${BACKEND_URL}${s.image_url}`} alt={s.name_nl} className="w-full h-32 object-cover" />
            )}
            <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-medium">{s.name_nl}</h3>
                <p className="text-white/40 text-xs">{s.name_en}</p>
              </div>
              <Switch data-testid={`service-active-toggle-${s.id}`} checked={s.is_active} onCheckedChange={() => toggleActive(s)} className="data-[state=checked]:bg-gold" />
            </div>
            <p className="text-gold font-serif text-lg mb-1">€{s.price}</p>
            <p className="text-white/40 text-xs mb-4">{s.duration} {t("services.minutesLabel")} &middot; volgorde {s.order}</p>
            <div className="flex gap-3">
              <button data-testid={`service-edit-${s.id}`} onClick={() => openEdit(s)} className="text-white/60 hover:text-gold"><Pencil size={16} /></button>
              <button data-testid={`service-delete-${s.id}`} onClick={() => setDeleteId(s.id)} className="text-white/60 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="service-form-dialog" className="bg-[#141414] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("admin.services.editService") : t("admin.services.addService")}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/70 text-sm">{t("admin.services.nameNl")}</Label><Input data-testid="service-form-name-nl" value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" required /></div>
              <div><Label className="text-white/70 text-sm">{t("admin.services.nameEn")}</Label><Input data-testid="service-form-name-en" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" required /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/70 text-sm">{t("admin.services.descriptionNl")}</Label><Textarea data-testid="service-form-desc-nl" value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" /></div>
              <div><Label className="text-white/70 text-sm">{t("admin.services.descriptionEn")}</Label><Textarea data-testid="service-form-desc-en" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-white/70 text-sm">{t("admin.services.price")}</Label><Input type="number" step="0.01" data-testid="service-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" required /></div>
              <div><Label className="text-white/70 text-sm">{t("admin.services.duration")}</Label><Input type="number" data-testid="service-form-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 0 })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" required /></div>
              <div><Label className="text-white/70 text-sm">{t("admin.services.order")}</Label><Input type="number" data-testid="service-form-order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="service-form-active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} className="data-[state=checked]:bg-gold" />
              <Label className="text-white/70 text-sm">{t("admin.services.isActive")}</Label>
            </div>
            <div>
              <Label className="text-white/70 text-sm">{t("admin.services.image")}</Label>
              {form.image_url ? (
                <div className="relative mt-1.5 w-fit">
                  <img src={`${BACKEND_URL}${form.image_url}`} alt="" className="h-24 rounded-md object-cover" />
                  <button type="button" data-testid="service-form-remove-image" onClick={() => setForm({ ...form, image_url: null })} className="absolute -top-2 -right-2 bg-black border border-white/20 rounded-full p-1 text-white/70 hover:text-red-400">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <label className="mt-1.5 flex items-center gap-2 border border-dashed border-white/20 rounded-md px-4 py-3 text-white/50 text-sm cursor-pointer hover:border-gold w-fit">
                  <ImagePlus size={16} />
                  {imageUploading ? t("common.loading") : t("admin.services.uploadImage")}
                  <input data-testid="service-form-image-input" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={imageUploading} />
                </label>
              )}
            </div>
            {error && <p data-testid="service-form-error" className="text-red-400 text-sm">{error}</p>}
            <Button type="submit" data-testid="service-form-submit" className="w-full bg-gold text-white hover:bg-gold-hover rounded-none">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#141414] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.delete")}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{t("admin.bookings.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/20 text-white">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="service-delete-confirm" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">{t("common.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
